import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogTitle,
} from '@codes/ui-kit/components/ui/alert-dialog';
import { Button } from '@codes/ui-kit/components/ui/button';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';

import { chatKeys, useChat, useChildChats, useDeleteChat, useSendMessage } from '@codes/chatbots';
import { useGlobalLoader } from '@codes/shared';

import { ChatCard, ChatInput, ChatbotSettings, ModelSelectionDialog, PromptSettings, RootChats } from '../components';

import type { ChatView, RagChatBody } from '@lemoncloud/ssocio-chatbots-api';

const SCROLL_THROTTLE_MS = 16; // 60fps

export const ChatPage = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const queryClient = useQueryClient();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedStrategy, setSelectedStrategy] = useState<string>('md1');

    const [sendingStates, setSendingStates] = useState<Record<string, boolean>>({});
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [chatToDelete, setChatToDelete] = useState<string | null>(null);
    const [areChatsReady, setAreChatsReady] = useState(false);

    const { setIsLoading } = useGlobalLoader();
    const deleteChat = useDeleteChat();

    const { data: selectedChat } = useChat(id || '');
    const { data: childChatsData, isLoading, error, refetch } = useChildChats({ rootId: id, limit: 20 });

    useEffect(() => {
        setIsLoading(isLoading);
        if (error) {
            toast({ title: t('ai.chat.load_error') });
        }
    }, [isLoading, error, setIsLoading, t]);

    useEffect(() => {
        const isEmptyArray = childChatsData?.data?.length === 0;
        const hasOnlyEmptyObjects = childChatsData?.data?.every(item => !Object.keys(item).length);

        if (isEmptyArray || hasOnlyEmptyObjects) {
            setDialogOpen(true);
        }
        setAreChatsReady(false);

        scrollRefs.current = new Array(childChatsData?.data?.length).fill(null);

        // Add a small delay to allow chat windows to render
        const timer = setTimeout(() => {
            const allLoaded = scrollRefs.current.every(ref => {
                if (!ref) {
                    return false;
                }
                const scrollContainer = ref.querySelector('.overflow-y-auto');
                return scrollContainer && scrollContainer.scrollHeight > 0;
            });
            setAreChatsReady(allLoaded);
        }, 100);

        return () => {
            clearTimeout(timer);
            scrollRefs.current = [];
            setAreChatsReady(false);
        };
    }, [childChatsData, id]); // Add id to dependencies

    const sendMessage = useSendMessage();

    const [inputValue, setInputValue] = useState('');

    const getGridColumns = (modelCount: number) => {
        switch (modelCount) {
            case 1:
                return 'grid-cols-1';
            case 2:
                return 'grid-cols-2 max-sm:grid-cols-1';
            case 3:
                return 'grid-cols-3 max-sm:grid-cols-1';
            case 4:
                return 'grid-cols-2 lg:grid-cols-4';
            case 5:
            case 6:
                return 'grid-cols-2 lg:grid-cols-3';
            default:
                return 'grid-cols-2 lg:grid-cols-4';
        }
    };

    const getCardHeight = (modelCount: number) => {
        if (modelCount <= 2) {
            return 'h-[calc(100vh-284px)]';
        }
        // if (modelCount <= 4) {
        //     return 'h-[600px]';
        // }
        return 'h-[500px]';
    };

    const updateChatMessages = async (chatId: string, newMessages: ChatView[]) => {
        await queryClient.setQueryData(chatKeys.list({ rootId: chatId, limit: -1 }), (oldData: any) => {
            if (!oldData) {
                return oldData;
            }

            const newFirstPage = {
                ...oldData.pages[0],
                data: [...(oldData.pages[0].data || []), ...newMessages],
            };
            return {
                ...oldData,
                pages: [newFirstPage, ...oldData.pages.slice(1)],
                pageParams: oldData.pageParams,
            };
        });
    };

    const handleMessageSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!inputValue.trim()) {
            return;
        }

        try {
            const body: RagChatBody = {
                input: inputValue,
                sid: selectedChat?.sid || selectedChat?.profile$?.sid,
            };
            // Initialize sending states for all chats
            const initialStates = childChatsData?.data.reduce((acc, chat) => {
                acc[chat.id] = true;
                return acc;
            }, {} as Record<string, boolean>);
            setSendingStates(initialStates || {});

            // Send message to all chats in parallel
            await Promise.all(
                childChatsData?.data.map(async chat => {
                    try {
                        const res = await sendMessage.mutateAsync({
                            groupId: chat.id as string,
                            params: {
                                strategy: selectedStrategy,
                            },
                            body,
                        });

                        await updateChatMessages(chat.id, res.list || []);
                    } catch (error: any) {
                        const errorResponse = error?.response?.data;
                        if (errorResponse?.list) {
                            await updateChatMessages(chat.id, errorResponse.list);
                        }
                    } finally {
                        setSendingStates(prev => ({ ...prev, [chat.id]: false }));
                    }
                }) || []
            );

            setInputValue('');
        } catch (error) {
            console.error('Failed to send messages:', error);
            toast({ title: t('ai.chat.send_error') });
        }
    };

    // Add new state and refs for scroll syncing
    const scrollRefs = useRef<(HTMLDivElement | null)[]>([]);
    const isScrolling = useRef(false);
    const lastScrollTime = useRef(0);

    const updateScrollRef = useCallback(
        (el: HTMLDivElement | null, index: number) => {
            if (scrollRefs.current.length !== childChatsData?.data?.length) {
                scrollRefs.current = new Array(childChatsData?.data?.length).fill(null);
            }
            scrollRefs.current[index] = el;

            // Check if all refs are set
            const allRefsSet = scrollRefs.current.every(
                (ref, idx) => idx >= (childChatsData?.data?.length || 0) || ref !== null
            );
            setAreChatsReady(allRefsSet);
        },
        [childChatsData?.data?.length]
    );

    // Handle scroll sync
    const handleScroll = useCallback(
        (index: number, event: React.UIEvent<HTMLDivElement>) => {
            const now = Date.now();
            if (isScrolling.current || now - lastScrollTime.current < SCROLL_THROTTLE_MS || !areChatsReady) {
                return;
            }

            const target = event.target as HTMLDivElement;
            const scrollRatio = target.scrollTop / (target.scrollHeight - target.clientHeight);

            isScrolling.current = true;
            lastScrollTime.current = now;

            scrollRefs.current.forEach((cardRef, i) => {
                if (i !== index && cardRef) {
                    const scrollContainer = cardRef.querySelector('.overflow-y-auto');
                    if (scrollContainer) {
                        const scrollHeight = scrollContainer.scrollHeight - scrollContainer.clientHeight;
                        scrollContainer.scrollTop = scrollHeight * scrollRatio;
                    }
                }
            });

            isScrolling.current = false;
        },
        [areChatsReady]
    );

    const isAnySending = Object.values(sendingStates).some(state => state);

    const handleDeleteClick = (chatId: string) => {
        setChatToDelete(chatId);
        setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!chatToDelete) {
            return;
        }

        try {
            setIsLoading(true);
            await deleteChat.mutateAsync(chatToDelete, {
                onSuccess: async () => {
                    setIsDeleteDialogOpen(false);
                    await queryClient.invalidateQueries(chatKeys.list({ rootId: id }));
                    toast({ title: t('ai.chat.delete_success') });
                },
            });
        } catch (error) {
            console.error('Failed to delete chat:', error);
            toast({ title: t('ai.chat.delete_error') });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="flex-1 bg-default pt-[38px] pb-[42px] px-9 max-xs:px-6 flex justify-center">
                <div className="max-w-[1500px] w-full grid grid-cols-[1fr_3fr] max-sm:grid-cols-1 gap-6">
                    <RootChats />
                    <div className="flex-1 flex flex-col">
                        {error ? (
                            <div className="h-full flex items-center justify-center">
                                <div className="text-center space-y-4">
                                    <h2 className="text-2xl font-bold">{t('ai.chat.load_error')}</h2>
                                    <Button onClick={() => refetch()} variant="outline">
                                        {t('common.retry')}
                                    </Button>
                                </div>
                            </div>
                        ) : id ? (
                            <>
                                <div className="mb-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h1 className="text-[20px] font-medium">
                                                {t('ai.chat.compare_title', { name: selectedChat?.name || 'AI' })}
                                            </h1>
                                            <p className="text-sub text-sm">
                                                {t('ai.chat.compare_description', {
                                                    count: childChatsData?.data?.length,
                                                })}
                                            </p>
                                        </div>
                                        <PromptSettings />
                                    </div>
                                </div>

                                <div className={`grid ${getGridColumns(childChatsData?.data?.length)} gap-6`}>
                                    {childChatsData?.data.map((chat, index) => (
                                        <ChatCard
                                            key={`chat_card_${chat.id}_${index}`}
                                            chat={chat}
                                            index={index}
                                            isSending={sendingStates[chat.id]}
                                            onDelete={handleDeleteClick}
                                            onScroll={handleScroll}
                                            scrollRef={el => updateScrollRef(el, index)}
                                            cardHeight={getCardHeight(childChatsData?.data?.length)}
                                        />
                                    ))}
                                </div>

                                <div className="pl-60 mt-auto fixed bottom-0 right-0 w-full bg-background border-t border-chatbot-line flex justify-center">
                                    <div className="w-full max-w-[720px] flex items-center justify-center gap-4 p-4">
                                        <ChatbotSettings
                                            selectedStrategy={selectedStrategy}
                                            setSelectedStrategy={setSelectedStrategy}
                                        />
                                        <div className="flex-1">
                                            <ChatInput
                                                value={inputValue}
                                                onChange={e => setInputValue(e.target.value)}
                                                onSubmit={handleMessageSubmit}
                                                isDisabled={isAnySending}
                                                isLoading={isAnySending}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="h-full flex items-center justify-center">
                                <div className="text-center space-y-4">
                                    <h2 className="text-[20px] font-medium">{t('ai.chat.select_chat')}</h2>
                                    <p className="text-sub text-sm">{t('ai.chat.select_chat_description')}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ModelSelectionDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                forceOpen={childChatsData?.data?.length === 0}
            />

            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogTitle>{t('ai.chat.delete_title')}</AlertDialogTitle>
                    <AlertDialogDescription>{t('ai.chat.delete_confirmation')}</AlertDialogDescription>
                    <AlertDialogFooter>
                        <AlertDialogCancel type="button" onClick={() => setIsDeleteDialogOpen(false)}>
                            {t('common.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmDelete}>{t('common.delete')}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
